import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { getPostLoginPath } from '../utils/rolePaths';
import { PORTAL_ROLES } from '../utils/navPermissions';
import { resolveNotificationLink } from '../utils/notificationLinks';
import { isNotificationUnread, markNotificationRead } from '../utils/notifications';
import api, { getUserAvatarUrl, getSchoolAssetUrl, getPlatformAssetUrl } from '../utils/api';
import { formatDateTime } from '../utils/appDateTime';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { MessageCircle, Bell, User, Settings, LogOut, Search, Menu, X, Sun, Moon, MessageSquare, School } from 'lucide-react';
import BranchPickerMenu from './settings/BranchPickerMenu';
import ChatDropdownContent from './ChatDropdown';
import { useToast } from '../hooks/use-toast';
import { canSubmitFeedback } from '../utils/feedbackPermissions';
import { isMobileContainer } from '../utils/device';
import { isSchoolFeatureEnabled } from '../utils/schoolFeatures';
import { canAccessSchoolChat, isCrmRole } from '../utils/crmPermissions';

const NOTIFICATION_PAGE_SIZE = 10;

export default function TopNav({ isSidebarOpen, setSidebarOpen, schoolBranding = null, platformBranding = null }) {
  const { user, logout, canOpenSettings, isSchoolObserver: observerMode } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { toast } = useToast();
  const homePath = getPostLoginPath(user?.role_slug, user);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const mobileSearchRef = useRef(null);
  const notifListRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const loadArmedRef = useRef(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(false);
  const [loadingMoreNotifications, setLoadingMoreNotifications] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);

  const schoolId = user?.school_id;
  const showSchoolChat = canAccessSchoolChat(user);

  useEffect(() => {
    setSearchQuery(searchParams.get('q') || '');
  }, [searchParams]);

  useEffect(() => {
    if (showMobileSearch) {
      setTimeout(() => mobileSearchRef.current?.focus(), 0);
    }
  }, [showMobileSearch]);

  const resetNotifications = useCallback(async () => {
    loadArmedRef.current = true;
    try {
      const res = await api.get('/notifications', {
        params: { limit: NOTIFICATION_PAGE_SIZE, offset: 0 },
      });
      const data = res.data?.data || {};
      setNotifications(data.items || []);
      setUnreadCount(Number(data.unread_count || 0));
      setHasMoreNotifications(Boolean(data.has_more));
    } catch (e) {
      setNotifications([]);
      setUnreadCount(0);
      setHasMoreNotifications(false);
    }
  }, []);

  const pollNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications', {
        params: { limit: NOTIFICATION_PAGE_SIZE, offset: 0 },
      });
      const data = res.data?.data || {};
      const freshItems = data.items || [];
      setUnreadCount(Number(data.unread_count || 0));
      let listWasEmpty = false;
      setNotifications((prev) => {
        if (prev.length === 0) {
          listWasEmpty = true;
          return freshItems;
        }
        const existingIds = new Set(prev.map((n) => n.id));
        const toPrepend = freshItems.filter((n) => !existingIds.has(n.id));
        if (toPrepend.length === 0) {
          const freshById = new Map(freshItems.map((n) => [n.id, n]));
          return prev.map((n) => (freshById.has(n.id) ? { ...n, ...freshById.get(n.id) } : n));
        }
        return [...toPrepend, ...prev];
      });
      if (listWasEmpty) {
        setHasMoreNotifications(Boolean(data.has_more));
      }
    } catch (e) {
      // Keep existing list on poll failure
    }
  }, []);

  const loadMoreNotifications = useCallback(async () => {
    if (loadingMoreRef.current || !hasMoreNotifications || !loadArmedRef.current) return;
    loadArmedRef.current = false;
    loadingMoreRef.current = true;
    setLoadingMoreNotifications(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 400));
      const offset = notifications.length;
      const res = await api.get('/notifications', {
        params: { limit: NOTIFICATION_PAGE_SIZE, offset },
      });
      const data = res.data?.data || {};
      const items = data.items || [];
      setNotifications((prev) => {
        const existingIds = new Set(prev.map((n) => n.id));
        const appended = items.filter((n) => !existingIds.has(n.id));
        return [...prev, ...appended];
      });
      setHasMoreNotifications(Boolean(data.has_more));
      if (typeof data.unread_count !== 'undefined') {
        setUnreadCount(Number(data.unread_count || 0));
      }
    } catch (e) {
      // Keep existing list on append failure
    } finally {
      loadingMoreRef.current = false;
      setLoadingMoreNotifications(false);
    }
  }, [hasMoreNotifications, notifications.length]);

  const handleNotificationsScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom > 80) {
        loadArmedRef.current = true;
        return;
      }
      if (distanceFromBottom <= 40) {
        loadMoreNotifications();
      }
    },
    [loadMoreNotifications]
  );

  const fetchChatUnread = async () => {
    try {
      const res = await api.get('/chat/unread-count');
      const count = Number(res.data?.data?.unread_contacts_count || 0);
      setChatUnreadCount(count);
    } catch (e) {
      setChatUnreadCount(0);
    }
  };

  useEffect(() => {
    if (user?.id) {
      resetNotifications();
      if (showSchoolChat) {
        fetchChatUnread();
      }
      const id = setInterval(() => {
        if (document.visibilityState === 'visible') {
          pollNotifications();
          if (showSchoolChat) {
            fetchChatUnread();
          }
        }
      }, 30000);
      return () => clearInterval(id);
    }
  }, [user?.id, user?.active_branch_id, showSchoolChat, resetNotifications, pollNotifications]);

  useEffect(() => {
    if (notifOpen) {
      resetNotifications();
    }
  }, [notifOpen, resetNotifications]);

  useEffect(() => {
    const handler = () => resetNotifications();
    window.addEventListener('notificationsRead', handler);
    return () => window.removeEventListener('notificationsRead', handler);
  }, [resetNotifications]);

  useEffect(() => {
    if (showSchoolChat && chatOpen) {
      fetchChatUnread();
    }
  }, [chatOpen, showSchoolChat]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchQuery?.trim();
    if (q) navigate(`/search?q=${encodeURIComponent(q)}`);
    else navigate(homePath);
  };

  const handleProfile = () => navigate('/profile');
  const handleSettings = () => navigate('/settings');
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNotificationClick = async (notif) => {
    setNotifOpen(false);
    const target = resolveNotificationLink(user, notif, homePath);
    try {
      if (isNotificationUnread(notif)) {
        await markNotificationRead(notif.id);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, is_read: 1, read_at: new Date().toISOString() } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch {
      // Mark-read failure should not block navigation
    } finally {
      if (target) {
        navigate(target);
      }
    }
  };

  const handleMarkAllRead = async () => {
    await api.post('/notifications/read-all');
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1, read_at: n.read_at || new Date().toISOString() })));
    setUnreadCount(0);
  };

  const userAvatarUrl = getUserAvatarUrl(user?.avatar);
  const initials = [user?.first_name?.[0], user?.last_name?.[0]].filter(Boolean).join('').toUpperCase() || 'U';

  const isNativeMobile = isMobileContainer();
  const showSettings = Boolean(canOpenSettings && canOpenSettings());
  const isPortalUser = PORTAL_ROLES.includes(user?.role_slug);
  const showSearch = !isPortalUser;
  const isCrmSearch = isCrmRole(user) && !observerMode;
  const searchPlaceholder = isCrmSearch
    ? 'Search tickets, employees, feedback, enquiries, schools...'
    : 'Search students, teachers, classes...';

  const isSuperAdmin = user?.role_slug === 'super_admin' && !observerMode;
  const resolvedSchoolId = schoolBranding?.id ?? user?.school_id;
  const hasSchoolBrand = Boolean(schoolBranding && resolvedSchoolId);
  const showSchoolBrand = hasSchoolBrand && (!isSuperAdmin || observerMode);
  const showPlatformBranding = (isSuperAdmin || !user?.school_id) && !observerMode;
  const platformLogoUrl = showPlatformBranding
    ? getPlatformAssetUrl(platformBranding?.logo, platformBranding?.version)
    : null;
  const defaultProductLogo = `${process.env.PUBLIC_URL || ''}/images/school-analytics-product-logo.png`;
  const platformName = platformBranding?.name?.trim() || '';
  const hasCustomPlatformLogo = Boolean(platformLogoUrl);
  const platformLogoAlt = platformName || 'School Analytics CPRawat — Smart insights. Better education.';

  const nativeIconBtn = isNativeMobile ? 'h-8 w-8' : 'h-9 w-9';
  const nativeIconSize = isNativeMobile ? 'h-4 w-4' : 'h-5 w-5';

  const renderMobileBrand = () => {
    if (showSchoolBrand) {
      return (
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-9 w-9 rounded-lg border border-border bg-muted shrink-0 overflow-hidden flex items-center justify-center">
            {schoolBranding.logo ? (
              <img
                src={getSchoolAssetUrl(schoolBranding.logo, resolvedSchoolId)}
                alt=""
                className="h-full w-full object-contain p-0.5"
              />
            ) : (
              <School className="h-4 w-4 text-muted-foreground" aria-hidden />
            )}
          </div>
          <span className="font-semibold text-sm text-foreground truncate" title={schoolBranding.name}>
            {schoolBranding.name}
          </span>
        </div>
      );
    }
    if (showPlatformBranding) {
      if (hasCustomPlatformLogo && platformName) {
        return (
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-9 w-9 rounded-lg border border-border bg-muted shrink-0 overflow-hidden flex items-center justify-center">
              <img src={platformLogoUrl} alt="" className="h-full w-full object-contain p-0.5" />
            </div>
            <span className="font-semibold text-sm text-foreground truncate" title={platformName}>
              {platformName}
            </span>
          </div>
        );
      }
      return (
        <img
          src={hasCustomPlatformLogo ? platformLogoUrl : defaultProductLogo}
          alt={platformLogoAlt}
          className="h-9 max-w-[140px] object-contain object-left"
        />
      );
    }
    return null;
  };

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
      <div
        className={`flex items-center gap-2 ${
          isNativeMobile ? 'pt-safe min-h-[4rem] px-3' : 'h-14 px-4 lg:px-6'
        }`}
      >
      {/* Mobile sidebar toggle — hidden in native app (uses bottom nav More) */}
      {!isNativeMobile && (
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden shrink-0 text-foreground hover:bg-accent"
        onClick={() => setSidebarOpen(!isSidebarOpen)}
        aria-label="Toggle sidebar"
      >
        {isSidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </Button>
      )}

      {/* Mobile branding — platform logo for CRM, school logo for school users */}
      {!showMobileSearch && (
        <div className="lg:hidden flex items-center min-w-0 flex-1">
          {renderMobileBrand()}
        </div>
      )}

      {/* Global search — centered on md+ (staff only) */}
      {showSearch ? (
      <div className="hidden md:flex flex-1 min-w-0 justify-center px-2">
        <form onSubmit={handleSearchSubmit} className="w-full max-w-xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 bg-muted border-border text-foreground placeholder:text-muted-foreground"
            />
          </div>
        </form>
      </div>
      ) : null}

      {showSearch && showMobileSearch && (
        <form onSubmit={handleSearchSubmit} className="md:hidden flex-1 min-w-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={mobileSearchRef}
              type="search"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9 h-9 bg-muted border-border text-foreground placeholder:text-muted-foreground"
            />
            <button
              type="button"
              onClick={() => setShowMobileSearch(false)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Close search"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </form>
      )}

      <div className={`flex items-center shrink-0 ml-auto ${isNativeMobile ? 'gap-0.5' : 'gap-2'}`}>
        {!isNativeMobile && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={`${nativeIconBtn} shrink-0 text-muted-foreground hover:text-foreground hover:bg-accent`}
          onClick={() => {
            toggleTheme().catch(() => {
              toast({ title: 'Error', description: 'Could not save theme preference', variant: 'destructive' });
            });
          }}
          title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark' ? <Sun className={nativeIconSize} /> : <Moon className={nativeIconSize} />}
        </Button>
        )}

        {!showMobileSearch && user?.role_slug === 'school_admin' && isSchoolFeatureEnabled(user, 'branches') ? (
          isNativeMobile ? (
            <BranchPickerMenu variant="mobile" align="end" />
          ) : (
            <BranchPickerMenu variant="compact" align="end" className="hidden sm:block" />
          )
        ) : null}

        {!showMobileSearch && (
          <>
            {showSearch ? (
            <Button
              variant="ghost"
              size="icon"
              className={`${nativeIconBtn} text-muted-foreground hover:text-foreground hover:bg-accent md:hidden`}
              onClick={() => setShowMobileSearch(true)}
              title="Search"
            >
              <Search className={nativeIconSize} />
            </Button>
            ) : null}

            {/* Chat */}
            {showSchoolChat && (
            <DropdownMenu onOpenChange={setChatOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`relative ${nativeIconBtn} text-muted-foreground hover:text-foreground hover:bg-accent`}
                  title="Chat"
                >
                  <MessageCircle className={nativeIconSize} />
                  {chatUnreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                      {chatUnreadCount > 9 ? '9+' : chatUnreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="p-0 border-0 bg-transparent shadow-none">
                <ChatDropdownContent open={chatOpen} onUnreadChanged={fetchChatUnread} />
              </DropdownMenuContent>
            </DropdownMenu>
            )}

            {/* Notifications */}
            <DropdownMenu onOpenChange={setNotifOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`${nativeIconBtn} text-muted-foreground hover:text-foreground hover:bg-accent relative`}
                  title="Notifications"
                >
                  <Bell className={nativeIconSize} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 bg-popover border-border text-popover-foreground">
                <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                  <span className="text-sm font-semibold text-foreground">Notifications</span>
                  {unreadCount > 0 && (
                    <button
                      type="button"
                      onClick={handleMarkAllRead}
                      className="text-xs text-blue-400 hover:text-blue-300"
                    >
                      Mark all read
                    </button>
                  )}
                </div>
                <div
                  ref={notifListRef}
                  className="max-h-80 overflow-y-auto"
                  onScroll={handleNotificationsScroll}
                >
                  {notifications.length === 0 ? (
                    <div className="px-3 py-6 text-sm text-muted-foreground text-center">No notifications</div>
                  ) : (
                    <>
                      {notifications.map((notif) => (
                        <DropdownMenuItem
                          key={notif.id}
                          onSelect={(e) => {
                            e.preventDefault();
                            handleNotificationClick(notif);
                          }}
                          className={`cursor-pointer focus:bg-accent ${
                            isNotificationUnread(notif) ? 'text-foreground' : 'text-muted-foreground'
                          }`}
                        >
                          <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium">{notif.title}</span>
                            {notif.message && <span className="text-xs text-muted-foreground">{notif.message}</span>}
                            {notif.created_at && (
                              <span className="text-[10px] text-muted-foreground">
                                {formatDateTime(notif.created_at)}
                              </span>
                            )}
                          </div>
                        </DropdownMenuItem>
                      ))}
                      {loadingMoreNotifications && (
                        <div className="px-3 py-2 text-xs text-muted-foreground text-center">Loading…</div>
                      )}
                    </>
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

          </>
        )}

        {/* Profile dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className={`relative rounded-full p-0 ${isNativeMobile ? 'h-8 w-8' : 'h-9 w-9'}`}>
              <Avatar className={`border-2 border-border ${isNativeMobile ? 'h-8 w-8' : 'h-9 w-9'}`}>
                {userAvatarUrl ? (
                  <AvatarImage src={userAvatarUrl} alt={user?.first_name} />
                ) : null}
                <AvatarFallback className="bg-muted text-foreground text-sm">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-popover border-border text-popover-foreground">
            <div className="px-2 py-2 border-b border-border">
              <p className="text-sm font-medium text-foreground truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              <p className="text-xs text-muted-foreground">{user?.role}</p>
            </div>
            <DropdownMenuItem onClick={handleProfile} className="cursor-pointer focus:bg-accent">
              <User className="mr-2 h-4 w-4" />
              Profile
            </DropdownMenuItem>
            {canSubmitFeedback(user) && (
              <DropdownMenuItem onClick={() => navigate('/my-feedback')} className="cursor-pointer focus:bg-accent">
                <MessageSquare className="mr-2 h-4 w-4" />
                My Feedback
              </DropdownMenuItem>
            )}
            {showSettings && (
              <DropdownMenuItem onClick={handleSettings} className="cursor-pointer focus:bg-accent">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator className="bg-border" />
            <DropdownMenuItem onClick={handleLogout} className="cursor-pointer focus:bg-red-500/20 focus:text-red-400">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      </div>
    </header>
  );
}
